/**
 * Backup file export/import.
 *
 * Persistent storage and installing to the home screen both reduce the
 * chance the browser throws data away, but neither survives a person
 * choosing "clear browsing data", and nothing on the web can. A file the
 * family holds is the only real answer, so this is the durability
 * backstop: it also moves a log between phones and rescues the iOS case
 * where Safari and the installed app keep separate storage.
 *
 * The file is plain JSON on purpose. It is the family's own record, they
 * may want to hand it to a pediatrician, and an encrypted blob they
 * cannot open is worse than useless during a hard week.
 */
import { getAllEvents, kvGet } from './db';
import { nowIso } from './time';
import type { Profile, Settings, TrackedEvent } from './types';

export const BACKUP_FORMAT = 'first-weeks-backup';
export const BACKUP_VERSION = 1;

export interface BackupFile {
  format: typeof BACKUP_FORMAT;
  version: number;
  exportedAt: string;
  profile: Profile | null;
  settings: Settings | null;
  events: TrackedEvent[];
}

export async function buildBackup(): Promise<BackupFile> {
  const [events, profile, settings] = await Promise.all([
    getAllEvents(),
    kvGet<Profile>('profile'),
    kvGet<Settings>('settings'),
  ]);
  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt: nowIso(),
    profile: profile ?? null,
    settings: settings ?? null,
    // Tombstones travel too, so importing cannot resurrect a deleted entry.
    events,
  };
}

export function backupFilename(at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `first-weeks-${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}.json`;
}

/**
 * Hand the file to the user. Uses the share sheet when available, since on
 * iOS that is the reliable way to get a file into Files or iCloud, and
 * falls back to a download link elsewhere.
 */
export async function downloadBackup(backup: BackupFile): Promise<'shared' | 'downloaded'> {
  const json = JSON.stringify(backup, null, 2);
  const name = backupFilename();
  const file = new File([json], name, { type: 'application/json' });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: name });
      return 'shared';
    } catch (err) {
      // A user cancelling the share sheet must not fall through to a
      // download they did not ask for.
      if (err instanceof DOMException && err.name === 'AbortError') return 'shared';
    }
  }

  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}

export class BackupParseError extends Error {}

/** Validates untrusted file contents into a BackupFile. */
export function parseBackup(text: string): BackupFile {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new BackupParseError('not-json');
  }
  if (typeof data !== 'object' || data === null) throw new BackupParseError('not-object');
  const b = data as Partial<BackupFile>;
  if (b.format !== BACKUP_FORMAT) throw new BackupParseError('wrong-format');
  if (typeof b.version !== 'number' || b.version > BACKUP_VERSION) {
    throw new BackupParseError('newer-version');
  }
  if (!Array.isArray(b.events)) throw new BackupParseError('no-events');

  const valid = (e: unknown): e is TrackedEvent => {
    if (typeof e !== 'object' || e === null) return false;
    const ev = e as Partial<TrackedEvent>;
    return (
      typeof ev.id === 'string' &&
      typeof ev.at === 'string' &&
      typeof ev.updatedAt === 'string' &&
      typeof ev.type === 'string' &&
      ['feed', 'diaper', 'weight', 'pump', 'sleep'].includes(ev.type)
    );
  };

  return {
    format: BACKUP_FORMAT,
    version: b.version,
    exportedAt: typeof b.exportedAt === 'string' ? b.exportedAt : nowIso(),
    profile: (b.profile as Profile | null) ?? null,
    settings: (b.settings as Settings | null) ?? null,
    events: b.events.filter(valid),
  };
}
